import { Modal, ModalTitle } from "@healthos/ui";
import { Clock, User } from "@phosphor-icons/react";
import { useState } from "react";
import {
  CONTACT_CENTRE_SHIFT_HOURS,
  type ContactCentreSettingsTab,
  type ContactCentreShiftHours,
  type ContactCentreWorkingHours,
  contactCentreTimeOptions,
  EMPTY_CONTACT_CENTRE_WORKING_HOURS,
} from "./contact-centre-account";

const TIME_OPTIONS = contactCentreTimeOptions();

const TABS: Array<{ id: ContactCentreSettingsTab; label: string; Icon: typeof User }> = [
  { id: "user", label: "User", Icon: User },
  { id: "settings", label: "Settings", Icon: Clock },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  email: string | null;
};

export function ContactCentreSettingsModal({ open, onOpenChange, userId, email }: Props) {
  const [tab, setTab] = useState<ContactCentreSettingsTab>("user");
  const [hours, setHours] = useState<ContactCentreWorkingHours>(EMPTY_CONTACT_CENTRE_WORKING_HOURS);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) setTab("user");
        onOpenChange(next);
      }}
      size="xl"
      contentClassName="overflow-hidden p-0"
    >
      <ModalTitle className="sr-only">Contact Centre account</ModalTitle>
      <div className="flex min-h-[28rem] flex-col sm:grid sm:grid-cols-[13.5rem_minmax(0,1fr)]">
        <nav
          aria-label="Account sections"
          className="flex gap-1 border-b border-border-default bg-surface-ambient/80 px-3 py-3 sm:flex-col sm:border-b-0 sm:border-r sm:px-4 sm:py-6"
        >
          {TABS.map(({ id, label, Icon }) => {
            const selected = tab === id;
            return (
              <button
                key={id}
                type="button"
                aria-current={selected ? "page" : undefined}
                onClick={() => setTab(id)}
                className={`inline-flex min-h-11 items-center gap-2.5 rounded-md px-3 text-left text-sm font-medium tracking-[-0.005em] transition-[background-color,color,transform] duration-180 ease-out active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  selected
                    ? "bg-surface-elevated text-ink-primary shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-mist-dark)_40%,transparent)]"
                    : "text-ink-secondary hover:bg-surface-track/45"
                }`}
              >
                <Icon
                  size={16}
                  weight="regular"
                  aria-hidden="true"
                  className={selected ? "text-ink-brand-light" : "text-ink-tertiary"}
                />
                {label}
              </button>
            );
          })}
        </nav>
        <section className="flex min-w-0 flex-col px-6 py-6 sm:px-8 sm:py-7">
          {tab === "user" ? (
            <UserPanel userId={userId} email={email} />
          ) : (
            <SettingsPanel hours={hours} onChange={setHours} />
          )}
        </section>
      </div>
    </Modal>
  );
}

function UserPanel({ userId, email }: { userId: string | null; email: string | null }) {
  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-1.5">
        <p className="m-0 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink-quaternary">
          User
        </p>
        <h2 className="m-0 font-title text-2xl font-medium tracking-[-0.02em] text-ink-primary">
          Your account
        </h2>
      </header>
      <dl className="m-0 grid gap-6">
        <AccountField label="User id" value={userId} empty="User id is not available yet." />
        <AccountField label="Email" value={email} empty="Email is not available yet." />
      </dl>
    </div>
  );
}

function AccountField({
  label,
  value,
  empty,
}: {
  label: string;
  value: string | null;
  empty: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <dt className="font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink-tertiary">
        {label}
      </dt>
      <dd className="m-0 break-all font-sans text-base leading-relaxed text-ink-primary">
        {value ?? empty}
      </dd>
    </div>
  );
}

function SettingsPanel({
  hours,
  onChange,
}: {
  hours: ContactCentreWorkingHours;
  onChange: (next: ContactCentreWorkingHours) => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-1.5">
        <p className="m-0 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink-quaternary">
          Settings
        </p>
        <h2 className="m-0 font-title text-2xl font-medium tracking-[-0.02em] text-ink-primary">
          Your shift
        </h2>
        <p className="m-0 max-w-[42ch] text-sm leading-relaxed text-ink-secondary">
          These choices stay on this screen until we save them for your account.
        </p>
      </header>
      <div className="grid gap-6 sm:grid-cols-2">
        <LabeledSelect
          id="cc-working-start"
          label="Working hours start"
          value={hours.start}
          placeholder="Choose a start time"
          options={TIME_OPTIONS}
          onChange={(start) => onChange({ ...hours, start })}
        />
        <LabeledSelect
          id="cc-working-end"
          label="Working hours end"
          value={hours.end}
          placeholder="Choose an end time"
          options={TIME_OPTIONS}
          onChange={(end) => onChange({ ...hours, end })}
        />
        <LabeledSelect
          id="cc-shift-length"
          label="Shift length"
          value={hours.shiftHours === "" ? "" : String(hours.shiftHours)}
          placeholder="Choose a shift length"
          options={CONTACT_CENTRE_SHIFT_HOURS.map((value) => String(value))}
          optionLabel={(value) => `${value} hours`}
          className="sm:col-span-2"
          onChange={(value) =>
            onChange({
              ...hours,
              shiftHours: (CONTACT_CENTRE_SHIFT_HOURS as readonly number[]).includes(Number(value))
                ? (Number(value) as ContactCentreShiftHours)
                : "",
            })
          }
        />
      </div>
    </div>
  );
}

function LabeledSelect({
  id,
  label,
  value,
  placeholder,
  options,
  optionLabel,
  className,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: readonly string[];
  optionLabel?: (value: string) => string;
  className?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      <label
        htmlFor={id}
        className="font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink-tertiary"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 appearance-none rounded-md border border-border-strong/55 bg-surface-elevated px-3 text-sm text-ink-primary outline-none transition-[border-color] duration-180 focus-visible:border-teal-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabel ? optionLabel(option) : option}
          </option>
        ))}
      </select>
    </div>
  );
}
